import React, { useState, useEffect, useRef } from 'react';

import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import Modal from '@salesforce/design-system-react/components/modal';
import Button from '@salesforce/design-system-react/components/button';
import Combobox from '@salesforce/design-system-react/components/combobox';
import ColorPicker from '@salesforce/design-system-react/components/color-picker';
import Textarea from '@salesforce/design-system-react/components/textarea';
import Input from '@salesforce/design-system-react/components/input';
import comboboxFilterAndLimit from '@salesforce/design-system-react/components/combobox/filter';
import toast, { Toaster } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { addExchangeValidation } from './addExchangeValidation';
import {
  addNewExchange,
  ExchangeAccountsSelector,
} from '../../store/exchanges/exchangeAccountSlice';
import { Formik } from 'formik';
import { defiAccountTypes } from '../../constants/defiAccount';

const defaultTagOptions = [
  {
    id: 'trading',
    label: 'Trading',
  },
  {
    id: 'skating',
    label: 'Skating',
  },
  {
    id: 'tomb',
    label: 'Tomb',
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const leadSourceOptions = [
  { id: '1', label: 'Third Party Program', value: 'A0' },
  { id: '2', label: 'Cold Call', value: 'B0' },
  { id: '3', label: 'LinkedIn', value: 'C0' },
  { id: '4', label: 'Direct Mail', value: 'D0' },
  { id: '5', label: 'Other', value: 'E0' },
];

const accountProviders = [
  { id: '1', label: 'Binance', value: 'binance' },
  { id: '4', label: 'Binance US', value: 'binanceus' },
  { id: '2', label: 'Felix', value: 'felix' },
  { id: '3', label: 'Kraken', value: 'kraken' },
  { id: '5', label: 'Coinbase', value: 'coinbase' },
  { id: '6', label: 'Kucoin', value: 'kucoin' },
  { id: '7', label: 'Bitmart', value: 'bitmart' },
  { id: '8', label: 'CoinFlex', value: 'coinflex' },
  { id: '9', label: 'Huobi', value: 'huobi' },
  { id: '10', label: 'Poloniex Spot', value: 'poloniex' },
  { id: '11', label: 'Poloniex Futures', value: 'poloniexfutures' },
];

const AddExchange = (props) => {
  const { openModal, closeModal } = props;
  const [categorySelection, setCategorySelection] = useState([]);
  const [typeSelection, setTypeSelection] = useState([]);
  const [tagSelection, setTagSelection] = useState([]);
  const [tagInputText, setTagInputText] = useState('');
  const [providerSelection, setProviderSelection] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [showToast, setShowToast] = useState(false);
  const dispatch = useDispatch();
  const { loading, success, message } = useSelector(ExchangeAccountsSelector);
  const formikRef = useRef();

  useEffect(() => {
    setIsOpen(openModal);
  }, [openModal]);

  useEffect(() => {
    if (!showToast) return;
    success &&
      !loading &&
      toast.success(message, {
        id: 'add-contact',
      });
    setShowToast(false);
  }, [success, showToast, message, loading]);

  const toggleOpen = () => {
    resetFormValues();
    setTagSelection([]);
    closeModal();
  };

  const resetFormValues = () => {
    formikRef?.current?.resetForm();
    setTagSelection([]);
    setCategorySelection([]);
    setTypeSelection([]);
    setProviderSelection([]);
  };

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize={true}
      initialValues={{
        exchangeName: '',
        provider: '',
        apiKey: '',
        apiSecret: '',
        category: '',
        type: '',
        description: '',
        tags: '',
        color: '',
        memo: 'memo demo',
      }}
      validationSchema={addExchangeValidation}
      validateOnChange={true}
      validateOnBlur={true}
      // validateOnMount={true}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        setSubmitting(true);
        let data = {
          name: values.exchangeName.trim(),
          cefi_account_provider: values.provider.length ? values.provider[0].value : '',
          api_key: values.apiKey,
          api_secret: values.apiSecret,
          category: values.category.length ? values.category[0].label : '',
          description: values.description || '',
          type: values.type.length ? values.type[0].label : '',
          tags: tagSelection.map((item) => item.label).join(','),
          account_color: values.color || '',
          account_type: 'cefi',
        };

        if (values?.provider[0].value == 'bitmart') {
          data = {
            ...data,
            memo: values.memo,
          };
        }
        /**
         * check if key is empty, and remove empty key.
         */
        Object.keys(data).forEach((key) => {
          if (!data[key]) delete data[key];
        });

        dispatch(addNewExchange(data)).then((res) => {
          if (res?.payload?.data) {
            // setShowToast(true);
            resetForm();
            resetFormValues();
            closeModal();
          }
        });
      }}
    >
      {(props) => {
        const {
          values,
          touched,
          errors,
          isSubmitting,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        } = props;
        return (
          <div>
            <Toaster position="top-right" reverseOrder={false} />

            <Modal
              ariaHideApp={false}
              isOpen={isOpen}
              footer={[
                <Button
                  className="modal-cancel-button"
                  key="cancel"
                  label="Cancel"
                  onClick={toggleOpen}
                />,
                <IconSettings iconPath="/assets/icons">
                  <Button
                    className="modal-save-button"
                    key="save"
                    label={loading ? <span>Loading</span> : <span>Save</span>}
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                    variant="neutral"
                    iconCategory="utility"
                    iconName="save"
                    iconPosition="left"
                  />
                </IconSettings>,
              ]}
              onRequestClose={toggleOpen}
              heading="Add Exchange Account"
            >
              <section className="slds-p-around_large">
                <form
                  onSubmit={handleSubmit}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                >
                  <div className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center">
                    <div className="slds-col_padded slds-m-bottom_large">
                      <Input
                        id="exchangeName"
                        label="Name"
                        name="exchangeName"
                        placeholder="HFA - Quant Trading"
                        required
                        value={values.exchangeName || ''}
                        // onChange={handleChange}
                        onChange={(e) => {
                          setFieldValue('exchangeName', e.target.value);
                        }}
                        onBlur={handleBlur}
                        className={classNames(
                          errors.exchangeName && touched.exchangeName && 'slds-has-error'
                        )}
                      />
                      {errors.exchangeName && touched.exchangeName && (
                        <div className="slds-form-element__help">{errors.exchangeName}</div>
                      )}
                    </div>

                    <div className="slds-col_padded slds-m-bottom_large">
                      <IconSettings iconPath="/assets/icons">
                        <Combobox
                          id="exchange"
                          name="exchange"
                          events={{
                            onSelect: (event, data) => {
                              const selection =
                                data.selection.length === 0 ? providerSelection : data.selection;
                              setProviderSelection(selection);
                              if (selection[0].value === 'bitmart') {
                                if (values?.memo === 'memo demo') {
                                  setFieldValue('memo', '');
                                }
                              } else {
                                if (values?.memo === '') {
                                  setFieldValue('memo', 'memo demo');
                                }
                              }
                              setFieldValue('provider', selection);
                            },
                          }}
                          labels={{
                            label: 'Provider',
                            placeholder: 'Select Provider',
                          }}
                          required
                          options={accountProviders}
                          selection={providerSelection}
                          variant="readonly"
                          className={classNames(
                            errors.provider && touched.provider && 'slds-has-error'
                          )}
                        />
                      </IconSettings>
                      {errors.provider && touched.provider && (
                        <div className="slds-form-element__help">{errors.provider}</div>
                      )}
                    </div>
                  </div>

                  <div
                    className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center"
                    style={{
                      width: '50.9%',
                      display: providerSelection[0]?.value == 'bitmart' ? 'flex' : 'none',
                    }}
                  >
                    <div className="slds-col_padded slds-m-bottom_large">
                      <Input
                        id="memo"
                        label="Memo"
                        required
                        placeholder="Enter Memo"
                        value={values.memo || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={classNames(errors.memo && touched.memo && 'slds-has-error')}
                      />
                      {errors.memo && touched.memo && (
                        <div className="slds-form-element__help">{errors.memo}</div>
                      )}
                    </div>
                  </div>

                  <div className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center">
                    <div className="slds-col_padded slds-m-bottom_large">
                      <Input
                        id="apiKey"
                        label="API Key"
                        required
                        placeholder="Enter API Key"
                        value={values.apiKey || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={classNames(errors.apiKey && touched.apiKey && 'slds-has-error')}
                      />
                      {errors.apiKey && touched.apiKey && (
                        <div className="slds-form-element__help">{errors.apiKey}</div>
                      )}
                    </div>
                    <div className="slds-col_padded slds-m-bottom_large">
                      <Input
                        id="apiSecret"
                        label="API Secret"
                        required
                        placeholder="0x1234...."
                        value={values.apiSecret || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={classNames(
                          errors.apiSecret && touched.apiSecret && 'slds-has-error'
                        )}
                      />
                      {errors.apiSecret && touched.apiSecret && (
                        <div className="slds-form-element__help">{errors.apiSecret}</div>
                      )}
                    </div>
                  </div>

                  <div className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center">
                    <div className="slds-col_padded slds-m-bottom_large">
                      <IconSettings iconPath="/assets/icons">
                        <Combobox
                          events={{
                            onSelect: (event, data) => {
                              const selection =
                                data.selection.length === 0 ? categorySelection : data.selection;
                              setFieldValue('category', selection);
                              setCategorySelection(selection);
                            },
                          }}
                          labels={{
                            label: 'Category',
                            placeholder: 'Select Category',
                          }}
                          options={leadSourceOptions}
                          selection={categorySelection}
                          variant="readonly"
                        />
                      </IconSettings>
                    </div>
                    <div className="slds-col_padded slds-m-bottom_large">
                      <IconSettings iconPath="/assets/icons">
                        <Combobox
                          id="type"
                          name="type"
                          events={{
                            onSelect: (event, data) => {
                              const selection =
                                data.selection.length === 0 ? typeSelection : data.selection;
                              setTypeSelection(selection);
                              setFieldValue('type', selection);
                            },
                          }}
                          labels={{
                            label: 'Type',
                            placeholder: 'Select Type',
                          }}
                          options={defiAccountTypes}
                          selection={typeSelection}
                          variant="readonly"
                        />
                      </IconSettings>
                    </div>
                  </div>

                  <div className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center">
                    <div className="slds-col_padded" style={{ alignSelf: 'normal' }}>
                      <div className="slds-form-element">
                        <div className="slds-form-element__control">
                          <Textarea
                            id="description"
                            name="description"
                            label="Description"
                            placeholder="Enter description"
                            value={values.description || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="slds-col_padded">
                      <div className="slds-m-bottom_large">
                        <IconSettings iconPath="/assets/icons">
                          <Combobox
                            id="tags"
                            name="tags"
                            optionsAddItem={
                              tagInputText.length > 0
                                ? [
                                    {
                                      id: 'add-new-tag',
                                      label: (inputValue) => [
                                        inputValue && inputValue.length > 0 ? (
                                          <span style={{ marginLeft: '-30px' }}>
                                            Create "{inputValue}"
                                          </span>
                                        ) : (
                                          ''
                                        ),
                                      ],
                                    },
                                  ]
                                : []
                            }
                            events={{
                              onChange: (event, data) => {
                                setTagInputText(data.value);
                              },
                              onSelect: (event, data) => {
                                const { selection } = data;
                                if (selection && selection.length > 0) {
                                  if (
                                    selection[selection.length - 1].type &&
                                    selection[selection.length - 1].type === 'footer'
                                  ) {
                                    if (tagInputText.length > 0) {
                                      setTagSelection(tagSelection.concat({ label: tagInputText }));
                                      setTagInputText('');
                                    }
                                  } else {
                                    setTagSelection(selection);
                                  }
                                } else {
                                  setTagSelection(selection.pop());
                                }
                              },
                              onSubmit: (event, data) => {
                                if (!data.value.trim()) return;
                                setTagSelection(tagSelection.concat({ label: data.value }));
                                setTagInputText('');
                              },
                              onRequestRemoveSelectedOption: (event, data) => {
                                setTagSelection(data.selection);
                              },
                            }}
                            labels={{
                              label: 'Tags',
                              placeholder: 'Enter tags',
                            }}
                            options={comboboxFilterAndLimit({
                              inputValue: tagInputText,
                              limit: 5,
                              options: defaultTagOptions,
                              selection: tagSelection,
                            })}
                            value={tagInputText}
                            selection={tagSelection}
                            multiple
                          />
                        </IconSettings>
                        <div className="slds-m-top_medium">
                          <IconSettings iconPath="/assets/icons">
                            <ColorPicker
                              labels={{ label: 'Color' }}
                              id="color"
                              name="color"
                              className={'slds-form-element__label'}
                              menuPosition="overflowBoundaryElement"
                              value={values.color}
                              events={{
                                onChange: (event, { color }) => {
                                  setFieldValue('color', color);
                                  setFormData({
                                    ...formData,
                                    color,
                                  });
                                },
                              }}
                            />
                          </IconSettings>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </section>
            </Modal>
          </div>
        );
      }}
    </Formik>
  );
};

export default AddExchange;
